import {
  Card,
  Button,
  Col,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  Avatar
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate, getStatusNameInArabicAsWell } from "../../../../../../utils/utils";
import { colors } from "../../../../../../constants/colors";
import { SearchOutlined,UserOutlined } from "@ant-design/icons";
import {getServiceProvidersAction, updateStatusAction} from "../../../../../../redux/services/service-providers/serviceProvidersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import styles from "./Provider.module.scss";
import ServicesDialog from "./ServicesDialog";
import Actions from "../../../../../../redux/actions";
import { toastService } from "../../../../../common/toast-messages/toast";

const User = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { serviceProviders } = useSelector((state) => state.serviceProviders);
  const [searchValue, setSearchValue] = useState("");
  const [defaultPageSize, setDefaultPageSize] = useState(
    appConstConfig.DEFAULT_TABLE_PAGE_SIZE
  );
  const [defaultPageNo, setDefaultPageNo] = useState(
    appConstConfig.DEFAULT_TABLE_PAGE_NUMBER
  );
  /* table dependencies */

  const [openServicesDialog, setOpenServicesDialog] = useState(false);

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  /* filter dependencies */

  /* function definition to get table data */
  const handleGetTableData = (tempPageNo, tempPageSize, isUpdateStatus = false) => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : isUpdateStatus ? tempPageNo : defaultPageNo,
      limit: isUpdateStatus ? tempPageSize : defaultPageSize,
      search: debouncedSearchValue,
    };
    dispatch(getServiceProvidersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [defaultPageNo, defaultPageSize, debouncedSearchValue]);

  /* on status changed */
  const onStatusChanged = async (id, value, record) => {
    const dataToBeSent = {};
    dataToBeSent.selectedServiceProviderId = record?._id;
    dataToBeSent["status"] = value?.value;

    try {
      await dispatch(updateStatusAction(dataToBeSent, setLoading));
      handleGetTableData(defaultPageNo,defaultPageSize, true)
    } catch (error) {
      toastService.error(error)
    }
  };
  /* on status changed */

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Appointments.serviceProvider"),
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                shape="large"
                src={record?.logo_image}
                key={record?.logo_image}
                icon={<UserOutlined />}
              />
               {
                record?.name
            ? `${record?.name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("chats.email"),
        render: (_, record) =>
          record?.owner_details ? `${record?.owner_details?.email}` : "-",
      },
      {
        title: t("Modules.Notify.city"),
        dataIndex: "address",
        key: "address",
        render: (address) => `${address?.city?.name || "-"}`,
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "active":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "inactive":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "suspended":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "deleted":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                {getStatusNameInArabicAsWell(record?.status)}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Select
                options={[
                  { value: "active", label: t("CommonUtils.active")},
                  { value: "inactive", label: t("CommonUtils.inactive")},
                  { value: "suspended", label: t("CommonUtils.suspended")},
                  // {value:"deleted", label:"Delete"}
                ]}
                placeholder="Select Status"
                defaultValue={record?.status}
                style={{ width: 120 }}
                onChange={(id, value) => onStatusChanged(id, value, record)}
              />
            </>
          );
        },
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("AppModuleNames.Provider.Services"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewServices(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, [serviceProviders?.data]);
  /* initial dependencies rendering */

  const handleViewServices = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_ORGANIZATION_ID, {
        selectedOrganizationId: "",
      })
    );
    setOpenServicesDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedOrganizationId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_ORGANIZATION_ID, {
          selectedOrganizationId,
        })
      );
    }
  };

  return (
    <div className={styles.userContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={[24]} justify="space-between">
          <Col span={8}>
          <Card
              bordered
              hoverable
              style={{ 
                width: 240, padding: 0, borderRadius: 20,
                border: `1px solid ${colors.oceanBlue}`,
              }}
            >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={8}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<UserOutlined style={{ fontSize: 22 }} />}
                  />
                </Col>
                <Col span={16}>
                  <Flex vertical>
                    <Typography.Title level={4}>
                      {t("Modules.chatConversation.serviceProviders")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.electricBlue }}
                    >
                      {serviceProviders?.total || 0}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={8}  style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviders?.data}
            columns={columns}
            pagination={{
              total: serviceProviders?.total,
              current: defaultPageNo,
              pageSize: defaultPageSize,
              onChange: (pageNo, pageSize) => {
                setDefaultPageNo(pageNo);
                setDefaultPageSize(pageSize);
              },
              pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
              showSizeChanger: true,
            }}
          />
        )}
      </Flex>
      <ServicesDialog
        open={openServicesDialog}
        onClose={() => {
          setOpenServicesDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default User;
